/**
 * Client-side mirror of the API password policy (PasswordPolicyDto).
 *
 * The server is the real authority; this only lets the signup, reset, invitation and
 * change-password forms give live feedback before a round trip.
 */

import { useEffect, useState } from 'react';
import { signup } from './endpoints';
import type { PasswordPolicyDto } from './types';

/**
 * Used until `GET /signup/password-policy` answers, and permanently if it fails. Deliberately at
 * least as strict as the ASP.NET Core Identity defaults so the form never accepts something the
 * server will reject.
 */
export const DEFAULT_PASSWORD_POLICY: PasswordPolicyDto = {
  minimumLength: 10,
  requireUpper: true,
  requireLower: true,
  requireDigit: true,
  requireNonAlphanumeric: false,
  description: 'At least 10 characters, with upper case, lower case and a number.',
};

export interface PasswordRule {
  key: string;
  label: string;
  met: boolean;
}

export type PasswordStrengthTone = 'bad' | 'warn' | 'good';

export interface PasswordAssessment {
  /** Every rule the policy asks for, in a stable order, with its current state. */
  rules: PasswordRule[];
  /** True when every rule is satisfied — the form may submit. */
  satisfied: boolean;
  /** 0-100, drives the strength meter width. */
  score: number;
  label: string;
  tone: PasswordStrengthTone;
}

/** Evaluates a candidate password against a policy. Safe to call on every keystroke. */
export function assessPassword(value: string, policy: PasswordPolicyDto): PasswordAssessment {
  const rules: PasswordRule[] = [
    {
      key: 'length',
      label: `At least ${policy.minimumLength} characters`,
      met: value.length >= policy.minimumLength,
    },
  ];

  if (policy.requireUpper) {
    rules.push({ key: 'upper', label: 'An upper-case letter', met: /[A-Z]/.test(value) });
  }
  if (policy.requireLower) {
    rules.push({ key: 'lower', label: 'A lower-case letter', met: /[a-z]/.test(value) });
  }
  if (policy.requireDigit) {
    rules.push({ key: 'digit', label: 'A number', met: /[0-9]/.test(value) });
  }
  if (policy.requireNonAlphanumeric) {
    rules.push({
      key: 'symbol',
      label: 'A symbol, for example ! ? or #',
      met: /[^A-Za-z0-9]/.test(value),
    });
  }

  const satisfied = value.length > 0 && rules.every((rule) => rule.met);
  const metCount = rules.filter((rule) => rule.met).length;

  // Rules carry most of the weight; extra length above the minimum earns the rest.
  const ruleShare = rules.length === 0 ? 1 : metCount / rules.length;
  const lengthBonus = Math.min(1, Math.max(0, (value.length - policy.minimumLength) / 6));
  const score =
    value.length === 0 ? 0 : Math.round(Math.min(100, ruleShare * 78 + (satisfied ? lengthBonus * 22 : 0)));

  const tone: PasswordStrengthTone = !satisfied ? 'bad' : score >= 90 ? 'good' : 'warn';
  const label = !satisfied
    ? value.length === 0
      ? 'Enter a password'
      : 'Does not meet the policy yet'
    : score >= 90
      ? 'Strong'
      : score >= 80
        ? 'Good'
        : 'Acceptable';

  return { rules, satisfied, score, label, tone };
}

/**
 * Fetches the live policy once on mount, falling back to {@link DEFAULT_PASSWORD_POLICY} when the
 * endpoint is unreachable. The endpoint is anonymous, so this is safe on the public pages.
 */
export function usePasswordPolicy(): PasswordPolicyDto {
  const [policy, setPolicy] = useState<PasswordPolicyDto>(DEFAULT_PASSWORD_POLICY);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    signup
      .passwordPolicy(controller.signal)
      .then((result) => {
        // Guard against a partial body: a missing minimum length would let anything through.
        if (!active || !result || typeof result.minimumLength !== 'number') return;
        setPolicy(result);
      })
      .catch(() => {
        // Offline or route missing: the stricter defaults stand.
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  return policy;
}
